import { useEffect, useState } from "react";
import logo from "assets/logo/logo.png";
import './errorPage.css'
import { t } from "i18next";

// Animated blobs configuration
const BLOB_CONFIGS = [
  {
    base: {
      x: 0.1,
      y: 0.1,
      size: 0.6,
      color: "#93c5fd",
      opacity: 0.6,
      blur: 80,
    },
    speed: 0.38,
    amp: 600,
    freq: 2.2,
    phase: Math.random() * Math.PI * 2,
  },
  {
    base: {
      x: 0.7,
      y: 0.7,
      size: 0.5,
      color: "#60a5fa",
      opacity: 0.5,
      blur: 100,
    },
    speed: 0.33,
    amp: 700,
    freq: 2.1,
    phase: Math.random() * Math.PI * 2,
  },
  {
    base: {
      x: 0.5,
      y: 0.3,
      size: 0.4,
      color: "#67e8f9",
      opacity: 0.4,
      blur: 60,
    },
    speed: 0.35,
    amp: 650,
    freq: 2.3,
    phase: Math.random() * Math.PI * 2,
  },
  {
    base: {
      x: 0.2,
      y: 0.8,
      size: 0.3,
      color: "#818cf8",
      opacity: 0.4,
      blur: 60,
    },
    speed: 0.31,
    amp: 800,
    freq: 2.2,
    phase: Math.random() * Math.PI * 2,
  },
  {
    base: {
      x: 0.65,
      y: 0.25,
      size: 0.18,
      color: "#f9b71f",
      opacity: 0.18,
      blur: 40,
    },
    speed: 0.36,
    amp: 520,
    freq: 2.4,
    phase: Math.random() * Math.PI * 2,
  },
  {
    base: {
      x: 0.8,
      y: 0.15,
      size: 0.22,
      color: "#38bdf8",
      opacity: 0.22,
      blur: 50,
    },
    speed: 0.39,
    amp: 640,
    freq: 2.25,
    phase: Math.random() * Math.PI * 2,
  },
  {
    base: {
      x: 0.3,
      y: 0.7,
      size: 0.19,
      color: "#2563eb",
      opacity: 0.18,
      blur: 40,
    },
    speed: 0.37,
    amp: 610,
    freq: 2.35,
    phase: Math.random() * Math.PI * 2,
  },
  {
    base: {
      x: 0.4,
      y: 0.5,
      size: 0.21,
      color: "#a5b4fc",
      opacity: 0.16,
      blur: 55,
    },
    speed: 0.41,
    amp: 720,
    freq: 2.5,
    phase: Math.random() * Math.PI * 2,
  },
  {
    base: {
      x: 0.6,
      y: 0.2,
      size: 0.17,
      color: "#fbbf24",
      opacity: 0.13,
      blur: 45,
    },
    speed: 0.43,
    amp: 680,
    freq: 2.6,
    phase: Math.random() * Math.PI * 2,
  },
];

// Hook for animated blobs
function useAnimatedBlobs() {
  const [blobs, setBlobs] = useState(() =>
    BLOB_CONFIGS.map(() => ({
      x: Math.random(),
      y: Math.random(),
      scale: 0.8 + Math.random() * 0.4,
      borderRadius: "50%",
    }))
  );

  useEffect(() => {
    let frame;
    let startTime = performance.now();

    function animate(now) {
      const elapsed = now - startTime;
      setBlobs((prevBlobs) =>
        prevBlobs.map((blob, i) => {
          const config = BLOB_CONFIGS[i];
          const time = elapsed * config.speed * 0.001;
          const x =
            config.base.x +
            Math.sin(time * config.freq + config.phase) * (config.amp / 1000);
          const y =
            config.base.y +
            Math.cos(time * config.freq + config.phase) * (config.amp / 1000);
          const scale = 0.8 + Math.sin(time * 2 + config.phase) * 0.2;
          const borderRadius = `${
            40 + Math.sin(time * 3 + config.phase) * 20
          }%`;
          return { x, y, scale, borderRadius };
        })
      );
      frame = requestAnimationFrame(animate);
    }
    frame = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(frame);
  }, []);

  return blobs;
}

export default function ErrorPageUI() {
  const blobs = useAnimatedBlobs();

  const handleGoHome = () => {
    window.location.href = "/";
  };

  const handleRetry = () => {
    window.location.reload();
  };

  return (
    <>
      {/* <Head>
        <title>Something Went Wrong - Nexme</title>
        <link rel="icon" type="image/png" href="/favicon.png" />
        <meta
          name="description"
          content="We're working to fix this issue. Please try again."
        />
      </Head> */}

      <div
        className="error-splash-screen"
        style={{
          overflow: "hidden",
          background: "linear-gradient(120deg, #e0f2fe 0%, #bae6fd 100%)",
        }}
      >
        {/* Animated Fluid Gradient Mesh Background */}
        <div className="error-splash-bg">
          {BLOB_CONFIGS.map((cfg, i) => {
            const blob = blobs[i];
            const left = `calc(${blob.x * 100}% - ${cfg.base.size * 50}vw)`;
            const top = `calc(${blob.y * 100}% - ${cfg.base.size * 50}vw)`;
            const size = `${cfg.base.size * 100}vw`;
            return (
              <div
                key={i}
                style={{
                  position: "absolute",
                  left,
                  top,
                  width: size,
                  height: size,
                  background: cfg.base.color,
                  opacity: cfg.base.opacity,
                  borderRadius: blob.borderRadius,
                  filter: `blur(${cfg.base.blur}px)`,
                  transform: `scale(${blob.scale})`,
                  transition: "background 0.3s",
                  willChange: "transform, border-radius",
                  pointerEvents: "none",
                }}
              />
            );
          })}
        </div>

        <div className="error-splash-content">
          <div className="error-logo-container">
            <div className="error-logo-icon">
              <img
                src={logo}
                alt="Nexme Logo"
                width={96}
                height={96}
                className="error-logo-img"
              />
            </div>
          </div>

          <div className="error-message-container">
            <h1 className="error-title">{t("somethingWentWrong")}</h1>
            <p className="error-description">
                {t("errorDescription")}
            </p>
            <div className="error-actions">
              <button onClick={handleRetry} className="error-retry-btn">
                {t("tryAgain")}
              </button>
              <button onClick={handleGoHome} className="error-home-btn">
                {t("goHome")}
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
